/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable from "@/components/data-table";
import { ColumnVisibility } from "@/components/data-table/column-visiblity";
import { ConfirmActionPopup } from "@/components/popups/confirm-action";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchInput } from "@/components/ui/search-input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useBulkDeleteLabels,
  useDeleteLabel,
  useLabels,
} from "@/hooks/use-labels";
import { usePermission } from "@/hooks/use-premission";
import { AddLabelDialog } from "@/pages/labels/add-labels";
import { createLabelTableColumns } from "@/pages/labels/label-table-columns";
import { useMembersStore } from "@/stores/members.store";
import { ILabel, LabelType } from "@/types/label.types";

import Trash from "@/components/icons/trash";
import InitialPageVisitGuideModal from "@/components/page-visit-guide";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { AgGridReact } from "ag-grid-react";
import { ChevronDown, Plus } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

export interface LabelData {
  id: number;
  label: string;
  color: string;
  description: string;
  createdOn: Date;
  createdBy: string;
}

const tabsOptions = [
  {
    label: "Mailbox",
    value: LabelType.MAILBOX,
  },
  {
    label: "Sender Profile",
    value: LabelType.SENDER_PROFILE,
  },
  {
    label: "Email Health",
    value: LabelType.DNS,
  },
  {
    label: "PostMaster",
    value: LabelType.POSTMASTER,
  },
  {
    label: "Template",
    value: LabelType.TEMPLATE,
  },
];

export const Route = createFileRoute("/_private/labels")({
  component: LabelsPage,
  validateSearch: (search) =>
    z
      .object({
        tab: z.nativeEnum(LabelType).catch(LabelType.MAILBOX),
      })
      .parse(search),
});

export default function LabelsPage() {
  const selectedTab = useSearch({
    from: "/_private/labels",
    select: (params) => params.tab,
  });

  const [showAddLabel, setShowAddLabel] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);

  const { hasPermission } = usePermission();
  const navigate = useNavigate();
  const { data: labelData, isLoading: isLabelLoading } = useLabels(
    page,
    limit,
    selectedTab,
  );
  const { data, totalPages: total } = labelData || { data: [], totalPages: 0 };

  const { memberList, workspaceMembers } = useMembersStore();

  console.log(memberList, 'memberList');
  console.log(workspaceMembers, 'workspaceMembers');

  const gridRef = useRef<AgGridReact>(null);

  const [editingLabel, setEditingLabel] = useState<ILabel | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<ILabel | null>(null);
  const deleteLabel = useDeleteLabel();
  const bulkDeleteLabels = useBulkDeleteLabels();
  const [selectedRows, setSelectedRows] = useState<ILabel[]>([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);

  const handleEditClick = useCallback((label: ILabel) => {
    setEditingLabel(label);
    setShowAddLabel(true);
  }, []);

  const handleDeleteClick = useCallback((label: ILabel) => {
    setSelectedLabel(label);
    setShowDeleteConfirm(true);
  }, []);

  const getMember = useCallback(
    (id: string) => {
      const member = (memberList as any).data.find(
        (member: any) => member.public_metadata?.externalId === id,
      );
      return member;
    },
    [memberList],
  );

  const columnDefs = useMemo(() => {
    const columns = createLabelTableColumns(
      getMember,
      hasPermission,
      handleEditClick,
      handleDeleteClick,
    );

    if (!hasPermission("labels:edit") && !hasPermission("labels:delete")) {
      return columns.filter((col) => col.colId !== "actions");
    }

    return columns;
  }, [getMember, hasPermission, handleEditClick, handleDeleteClick]);

  const handleConfirmDelete = async () => {
    if (!selectedLabel) return;

    await deleteLabel.mutateAsync(selectedLabel._id);
    toast.success("Label deleted successfully");
    window.cioanalytics?.track("label_deleted", {
      labelName: selectedLabel.name,
      type: selectedTab,
    });
  };

  const onSelectionChanged = () => {
    const selectedNodes = gridRef?.current?.api.getSelectedNodes();
    const selectedData = selectedNodes?.map((node) => node.data) || [];
    setSelectedRows(selectedData);
  };

  const handleBulkDelete = async () => {
    await bulkDeleteLabels.mutateAsync(selectedRows.map((row) => row._id));
    window.cioanalytics?.track("label_bulk_deleted", {
      count: selectedRows.length,
      type: selectedTab,
    });
    toast.success("Labels deleted successfully");
  };

  const onFilterTextBoxChanged = useCallback(
    (searchTerm: string) => {
      if (gridRef.current?.api) {
        gridRef.current.api.setGridOption("quickFilterText", searchTerm);
      }
    },
    [gridRef],
  );

  const handleTabChange = useCallback(
    (value: LabelType) => {
      navigate({
        to: Route.to,
        search: {
          tab: value,
        },
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <h1 className="page-heading">Labels</h1>
        {hasPermission("labels:create") && (
          <Button
            onClick={() => setShowAddLabel(true)}
            variant="default"
            size="sm"
          >
            <Plus className="w-4 h-4" />
            Add Label
          </Button>
        )}
      </div>
      <div className="mb-4">
        <Tabs
          value={selectedTab}
          onValueChange={(value) => handleTabChange(value as LabelType)}
          className="w-full hidden xs:block"
        >
          <TabsList className="border">
            {tabsOptions.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="w-full px-3 justify-between xs:hidden capitalize"
            >
              {tabsOptions.find((tab) => tab.value === selectedTab)?.label}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
            {tabsOptions.map((tab) => (
              <DropdownMenuItem
                key={tab.value}
                onSelect={() => handleTabChange(tab.value)}
              >
                {tab.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Table Toolbar */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <SearchInput
          onSearch={onFilterTextBoxChanged}
          placeholder="Search by labels..."
          className=""
        />
        <div className="flex items-center gap-2">
          {selectedRows.length > 0 && hasPermission("labels:delete") && (
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowBulkDelete(true)}
                variant="destructive"
                size="sm"
                className="group"
              >
                <Trash className="w-4 h-4" />
                <span className="hidden xs:block">Bulk Delete</span>
              </Button>
            </div>
          )}
          <ColumnVisibility
            gridRef={gridRef}
            storageKey="labels-grid-columns"
            triggerClassName="[&>span]:hidden xs:[&>span]:block"
          />
        </div>
      </div>
      {showAddLabel && (
        <AddLabelDialog
          open={showAddLabel}
          labelType={selectedTab}
          onOpenChange={(open) => {
            setShowAddLabel(open);
            if (!open) setEditingLabel(null);
          }}
          editLabel={editingLabel || undefined}
        />
      )}

      <DataTable
        ref={gridRef}
        rowData={data ?? []}
        columnDefs={columnDefs}
        className="w-full"
        defaultColDef={{
          editable: false,
          resizable: true,
          suppressMovable: false,
          flex: 1,
        }}
        loading={isLabelLoading}
        paginationProps={{
          pageSize: 10,
          totalPages: total,
          currentPage: page,
          onPageChange: (page: number) => {
            setPage(page);
          },
        }}
        onSelectionChanged={onSelectionChanged}
      />

      {showDeleteConfirm && (
        <ConfirmActionPopup
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title="Delete Label"
          description={`Are you sure you want to delete "${selectedLabel?.name}"? This action cannot be undone.`}
          actionLabel="Delete"
          onConfirm={handleConfirmDelete}
        />
      )}

      {showBulkDelete && (
        <ConfirmActionPopup
          open={showBulkDelete}
          onOpenChange={setShowBulkDelete}
          title="Delete Labels"
          description={`Are you sure you want to delete ${selectedRows?.length} labels? This action cannot be undone.`}
          actionLabel="Delete"
          onConfirm={handleBulkDelete}
        />
      )}

      <InitialPageVisitGuideModal
        type={
          selectedTab === LabelType.MAILBOX
            ? "mailboxLabel"
            : selectedTab === LabelType.SENDER_PROFILE
              ? "senderProfileLabel"
              : selectedTab === LabelType.DNS
                ? "emailHealthLabel"
                : selectedTab === LabelType.POSTMASTER
                  ? "postmasterLabel"
                  : "templateLabel"
        }
      />
    </div>
  );
}
